<template>
  <div>
    <Loading v-if="isLoading" :type="3" />
    <div v-else>
      <GenericList
        :dataSet="filteredData"
        :binding="binding"
        :columnsMetaData="columnsMetaData"
        :noDataMessage="noDataMessage"
        :scrollable="true"
        class="h-auto"
      >
        <b-row
          slot="header"
          class="d-flex justify-content-between align-items-end mb-3"
        >
          <div class="col-12 col-md-8">
            <h4>
              {{ "sections.listSoftwaresByClientSection.title" | i18n }}
            </h4>
          </div>
          <div class="col-10 col-md-4">
            <SearchBar
              text="Recherche"
              :dataSet="softwares"
              :filter="filter"
              :filteringDelay="0"
              @filteringDone="handleFilteringDone"
            ></SearchBar>
          </div>
        </b-row>
        <b-row no-gutters slot="softwareCount" class="text-light mb-3">
          {{ this.filteredData.length }}
          {{ "details.softwares" | i18n }}
        </b-row>
      </GenericList>
      <GenericBtnAdd @click="addSoftware" />
    </div>
    <FormSoftwareSection
      :isShown="show"
      :action="$api.addSoftware"
      :title="'sections.softwareFormSection.title' | i18n"
      :clientId="'' + clientId"
      @successed-request="close"
      @canceled="close"
    />
  </div>
</template>
<script>
import {
  Loading,
  StateCircleColor,
  GenericList,
  GenericBtnAdd,
  SearchBar
} from "@/components";
import FormSoftwareSection from "../forms/FormSoftwareSection.vue";
/**
 * @description Section des logiciels intallés pour un client
 * @author Maxime Boisvert
 * @author Kevin Dubé
 * @author Alexandre Gallant
 * @vue-prop {Number} clientId - L'id du client
 * @vue-prop {String} pageType - le type de la page
 * @vue-data {Boolean} show - Définit si le modal est montré ou non
 * @vue-data {Boolean} isLoading - Si le chargement des données est en cours
 * @vue-data {String} noDataMessage - Message à afficher lorsqu'il n'y a pas de données
 * @vue-data {Object} binding - Les données de liaison
 * @vue-data {Object} columnsMetaData - Les données des colonnes
 * @vue-data {Array} softwares - Les logiciels
 * @vue-data {Array} filteredData - La liste filtré des données
 * @vue-data {Function} filter - La fonction pour le tri des données
 * @vue-data {Number} to - Le n ième logiciel à la fin de la page courante
 * @vue-data {Number} from - Le n ième logiciel au début de la page courante
 * @vue-data {Number} total - Nombre total de logiciels
 * @vue-data {String} nextPage - L'url de la prochaine page dans la pagination
 * @vue-data {String} prevPage - L'url de la précédente page dans la pagination
 *
 */
export default {
  name: "ListSoftwaresByClientSection",
  components: {
    Loading,
    GenericList,
    FormSoftwareSection,
    GenericBtnAdd,
    SearchBar
  },
  props: {
    clientId: {
      type: Number,
      required: true
    },
    pageType: {
      type: String,
      default: "dashboard"
    }
  },
  data() {
    return {
      show: false,
      isLoading: true,
      noDataMessage: this.$i18n.t("sections.GenericList.softwareNoData"),
      binding: this.getBinding(),
      columnsMetaData: this.getMetaData(),
      softwares: [],
      filteredData: [],
      filter: (s, data) => {
        return s.length != 0
          ? data.filter(
              d =>
                d.categorie.toLowerCase().includes(s.toLowerCase()) ||
                d.name.toLowerCase().includes(s.toLowerCase()) ||
                (this.binding.ip != undefined ? d.ip.includes(s) : false) ||
                d.serverName.toLowerCase().includes(s.toLowerCase())
            )
          : this.softwares;
      }
    };
  },
  methods: {
    /**
     * @description Permet de fermer le modal
     */
    close() {
      this.show = false;
    },
    /**
     * @description Permet d'ouvrir le modal
     */
    addSoftware() {
      this.show = true;
    },
    /**
     * @description Effectue l'actualisation des données
     */
    async updateData() {
      let requestSofwares = await this.$api.getSoftwaresByClient(this.clientId);

      let servers = (
        await this.$api.getServersByClientNotPaginated(this.clientId)
      ).data.data;

      const finalSoftwaresList = [];
      let softwares = requestSofwares.data.data;
      softwares.forEach(software => {
        let server = servers.find(s => s.id == software.serveur_id);
        finalSoftwaresList.push({
          id: software.id,
          name: software.nom_application,
          state: software.etat,
          serverName: server.nom,
          categorie: software.categorie,
          ip: server.ip,
          cpu: server.moyenne_CPU
        });
      });
      this.softwares = finalSoftwaresList;
      this.filteredData = finalSoftwaresList;
      this.isLoading = false;
    },
    /**
     * @description Obtient les binding selon le type de page
     * @returns {Object} Les binding de la page ClientDetails ou Dashboard
     */
    getBinding() {
      if (this.pageType === "dashboard") {
        return {
          state: {
            component: {
              type: StateCircleColor,
              props: s => {
                return {
                  state: s.state
                };
              }
            }
          },
          name: {},
          categorie: {},
          serverName: {},
          ip: {},
          cpu: {},
          details: {
            component: {
              type: "router-link",
              props: s => {
                return {
                  to: `/softwares/${s.id}`
                };
              }
            },
            transform: () => "Détails"
          }
        };
      } else {
        return {
          state: {
            component: {
              type: StateCircleColor,
              props: s => {
                return {
                  state: s.state
                };
              }
            }
          },
          name: {},
          serverName: {},
          details: {
            component: {
              type: "router-link",
              props: s => {
                return {
                  to: `/softwares/${s.id}`
                };
              }
            },
            transform: () => "Détails"
          }
        };
      }
    },
    /**
     * @description Obtient les metaData selon le type de page
     * @returns {Array} Les metadata de la page ClientDetails ou Dashboard
     */
    getMetaData() {
      if (this.pageType === "dashboard") {
        return [
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.state"),
            class: "col-2 col-md-1 text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.nom"),
            class: "col-7 col-md-5 col-lg-3 text-break"
          },
          {
            name: this.$i18n.t(
              "sections.listSoftwaresByClientSection.categorie"
            ),
            class: "col-2 d-none d-xl-block text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.serveur"),
            class: "col-3 col-lg-2 d-none d-md-block text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.ip"),
            class: "col-2 d-none d-lg-block text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.cpu"),
            class: "col-1 d-none d-lg-block text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.link"),
            class: "col-2 col-lg-1 text-break text-right"
          }
        ];
      } else {
        return [
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.state"),
            class: "col-2 text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.nom"),
            class: "col-7 col-md-5 text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.serveur"),
            class: "col-4 d-none d-md-block col-md-4 text-break"
          },
          {
            name: this.$i18n.t("sections.listSoftwaresByClientSection.link"),
            class: "col-3 col-md-1 text-break text-right"
          }
        ];
      }
    },
    /**
     * @description Permet d'ajuster la liste après une recherche
     * @param {Array} filteredData les données filtrées
     */
    handleFilteringDone(filteredData) {
      this.filteredData = filteredData;
    }
  },
  /**
   * @description Effectue l'initialisation des données
   */
  created() {
    this.updateData();
  }
};
</script>
